import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDiscordMembership,
  isDiscordLinked,
  type DiscordMembershipStatus,
} from "../services/DiscordService";
import { useUserContext } from "../context/UserContext";

export type DiscordGateReason =
  | "not-linked" // user has no Discord account linked
  | "not-in-guild" // linked but not in the DealHawk Discord server
  | "missing-role" // in the server but missing the member role
  | "error"; // unexpected error checking membership

export interface UseDiscordGateResult {
  // Run the check and call `onAccess` if everything is OK, otherwise open the
  // sync modal. Returns true when access was granted, false when the modal was
  // opened (or an error occurred).
  gate: (onAccess?: () => void | Promise<void>) => Promise<boolean>;
  // Modal state
  syncModalOpen: boolean;
  closeSyncModal: () => void;
  reason: DiscordGateReason | null;
  status: DiscordMembershipStatus | null;
  isChecking: boolean;
}

// Cached membership query key
export const membershipQueryKey = ["discord", "membership"] as const;

export function shouldPreloadDiscordMembership(user: ReturnType<typeof useUserContext>["user"]): boolean {
  return !!(user?.retailers && user.retailers.length > 1);
}

export function useDiscordMembershipStatus() {
  const { user } = useUserContext();
  const enabled = shouldPreloadDiscordMembership(user);
  const query = useQuery({
    queryKey: membershipQueryKey,
    queryFn: getDiscordMembership,
    enabled,
    staleTime: 60 * 1000,
    retry: 1,
  });

  return {
    ...query,
    enabled,
    hasIssue: enabled && !!query.data?.linked && !query.data.hasRole,
  };
}

/**
 * Hook that gates an action behind a Discord membership check.
 *
 * - Runs `GET /v1/discord/membership` and only fires `onAccess` when the user
 *   has Discord linked, is in the guild, and has the member role.
 * - Otherwise opens a sync modal (rendered by the consumer using
 *   `<DiscordSyncModal />`) prompting the user to (re)connect Discord.
 *
 * Membership is cached on the React Query client so repeated gate calls do not
 * hammer Discord; pass `force: true` to bypass the cache.
 */
export function useDiscordGate(): UseDiscordGateResult {
  const queryClient = useQueryClient();
  const { user } = useUserContext();

  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [reason, setReason] = useState<DiscordGateReason | null>(null);
  const [status, setStatus] = useState<DiscordMembershipStatus | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const closeSyncModal = useCallback(() => setSyncModalOpen(false), []);

  const gate = useCallback(
    async (onAccess?: () => void | Promise<void>): Promise<boolean> => {
      // Fast path: user has no Discord linked locally - skip the API call.
      if (!isDiscordLinked(user)) {
        setReason("not-linked");
        setStatus({ linked: false, inGuild: false, hasRole: false, ok: false });
        setSyncModalOpen(true);
        return false;
      }

      setIsChecking(true);
      try {
        const result = await queryClient.fetchQuery({
          queryKey: membershipQueryKey,
          queryFn: getDiscordMembership,
          staleTime: 60 * 1000,
        });
        setStatus(result);

        if (result.ok) {
          await onAccess?.();
          return true;
        }

        const nextReason: DiscordGateReason = !result.linked
          ? "not-linked"
          : !result.inGuild
          ? "not-in-guild"
          : "missing-role";
        setReason(nextReason);
        setSyncModalOpen(true);
        return false;
      } catch (err) {
        console.error("[useDiscordGate] membership check failed:", err);
        setReason("error");
        setSyncModalOpen(true);
        return false;
      } finally {
        setIsChecking(false);
      }
    },
    [queryClient, user]
  );

  return { gate, syncModalOpen, closeSyncModal, reason, status, isChecking };
}
